"use client";

import { motion, useReducedMotion } from "framer-motion";
import type { ReactNode } from "react";
import Image from "next/image";
import { cn } from "@/lib/utils";
import { crossfadeTransition } from "@/components/motion/constants";

interface CrossfadeImageStackProps {
  images: readonly { key: string; src: string; alt: string }[];
  activeIndex: number;
  className?: string;
  sizes?: string;
  priority?: boolean;
  overlay?: ReactNode;
}

/** Smooth opacity crossfade between stacked images — no mount/unmount flicker */
export function CrossfadeImageStack({
  images,
  activeIndex,
  className,
  sizes = "50vw",
  priority = false,
  overlay,
}: CrossfadeImageStackProps) {
  const reduced = useReducedMotion();
  const transition = crossfadeTransition(reduced);

  return (
    <div className={cn("relative overflow-hidden", className)}>
      {images.map((img, index) => (
        <motion.div
          key={img.key}
          className="absolute inset-0"
          animate={{ opacity: index === activeIndex ? 1 : 0 }}
          transition={transition}
          aria-hidden={index !== activeIndex}
        >
          <Image
            src={img.src}
            alt={index === activeIndex ? img.alt : ""}
            fill
            className="object-cover"
            sizes={sizes}
            priority={priority && index === 0}
          />
        </motion.div>
      ))}
      {overlay}
    </div>
  );
}
